import { invoke } from "./invoke";
import { sceneListName } from "./sceneLabel";
import { displayFileName } from "./fileNames";
import { matchMaybe } from "./maybe";
import {
  chapterFolderFromScenePath,
  isDefaultSceneFileName,
  sceneFileFromScenePath,
  type ZwChapter,
} from "./zwConfig";

export type ChroniclePlatform = "desktop" | "mobile" | "web";

export type ChronicleEntry = {
  seq: number;
  targetRef: string;
  head: string;
  parent?: string | null;
  tree: string;
  /** RFC 3339 UTC. */
  timestamp: string;
  message: string;
  sessionId?: string | null;
  signatureType?: string | null;
  attestation?: string | null;
  derivedFromSeq?: number | null;
  beaconPulse?: number | null;
  beaconValue?: string | null;
  paths?: string[];
  /** Human labels parallel to `paths`, snapshotted at append. */
  pathLabels?: string[];
  wordsDiff?: number;
  platform?: ChroniclePlatform | null;
  /** License / share email for the session that wrote this entry. */
  author?: string | null;
};

/** One local calendar day in the GitHub-style contribution grid. */
export type CalendarDayCell = {
  day: string;
  wordsDiff: number;
  eventCount: number;
  paths: string[];
  /** Display labels for `paths` (e.g. `Chapter 1, scene 1`). */
  pathLabels: string[];
};

export type CalendarMonthLabel = {
  label: string;
  /** Week column index (0-based) where this month label should start. */
  weekIndex: number;
};

export type CalendarHeatmap = {
  /**
   * Columns = weeks (oldest → newest). Each week is Sun…Sat; `null` pads
   * days after the end of the window.
   */
  weeks: (CalendarDayCell | null)[][];
  monthLabels: CalendarMonthLabel[];
  maxAbsDiff: number;
  startDay: string;
  endDay: string;
};

/** About one year of weeks, matching GitHub's contribution graph. */
const CALENDAR_WEEK_COUNT = 53;

export async function listChronicleEntries(
  rootPath: string,
): Promise<ChronicleEntry[]> {
  return invoke<ChronicleEntry[]>("read_chronicle_entries", { rootPath });
}

/** Local calendar day (YYYY-MM-DD) for an RFC 3339 timestamp. */
export function entryLocalDay(timestamp: string): string {
  const d = new Date(timestamp);
  if (Number.isNaN(d.getTime())) return timestamp.slice(0, 10);
  return formatLocalDay(d);
}

export function formatLocalDay(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function parseLocalDay(day: string): Date {
  const [y, m, d] = day.split("-").map(Number);
  return new Date(y ?? 0, (m ?? 1) - 1, d ?? 1);
}

function addLocalDays(d: Date, n: number): Date {
  const next = new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
  return next;
}

function startOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/**
 * Aggregate entries by local day, then lay out a GitHub-style calendar
 * (Sunday-first rows, ~53 weeks ending today).
 */
export function buildCalendarHeatmap(
  entries: readonly ChronicleEntry[],
  now = new Date(),
  weekCount = CALENDAR_WEEK_COUNT,
  chapters?: ZwChapter[],
): CalendarHeatmap {
  const byDay = new Map<string, CalendarDayCell>();

  for (const entry of entries) {
    const day = entryLocalDay(entry.timestamp);
    const existing = byDay.get(day);
    const paths = entry.paths ?? [];
    const labels = resolveEntryPathLabels(entry, chapters);
    if (typeof existing !== "undefined") {
      existing.wordsDiff += entry.wordsDiff ?? 0;
      existing.eventCount += 1;
      for (let i = 0; i < paths.length; i++) {
        const p = paths[i]!;
        if (existing.paths.includes(p)) continue;
        existing.paths.push(p);
        existing.pathLabels.push(labels[i] ?? pathLabel(p));
      }
    } else {
      byDay.set(day, {
        day,
        wordsDiff: entry.wordsDiff ?? 0,
        eventCount: 1,
        paths: [...paths],
        pathLabels: [...labels],
      });
    }
  }

  const end = startOfLocalDay(now);
  let start = addLocalDays(end, -((weekCount - 1) * 7));
  // Align to Sunday (GitHub contribution graph).
  while (start.getDay() !== 0) {
    start = addLocalDays(start, -1);
  }

  const weeks: (CalendarDayCell | null)[][] = [];
  let cursor = new Date(start);
  while (weeks.length < weekCount || cursor <= end) {
    const week: (CalendarDayCell | null)[] = [];
    for (let dow = 0; dow < 7; dow++) {
      if (cursor > end) {
        week.push(null);
      } else {
        const key = formatLocalDay(cursor);
        week.push(
          byDay.get(key) ?? {
            day: key,
            wordsDiff: 0,
            eventCount: 0,
            paths: [],
            pathLabels: [],
          },
        );
      }
      cursor = addLocalDays(cursor, 1);
    }
    weeks.push(week);
    if (cursor > end) break;
  }

  const trimmed =
    weeks.length > weekCount ? weeks.slice(weeks.length - weekCount) : weeks;

  let maxAbsDiff = 0;
  for (const week of trimmed) {
    for (const cell of week) {
      if (cell === null) continue;
      maxAbsDiff = Math.max(maxAbsDiff, Math.abs(cell.wordsDiff));
    }
  }

  // Label a week only when it contains the 1st of a month. That skips the
  // leading partial month (e.g. Sep at both ends of a year window) so the
  // active month is named once, near the end.
  const monthLabels: CalendarMonthLabel[] = [];
  for (let wi = 0; wi < trimmed.length; wi++) {
    const week = trimmed[wi];
    if (typeof week === "undefined") continue;
    for (const cell of week) {
      if (cell === null) continue;
      const date = parseLocalDay(cell.day);
      if (date.getDate() !== 1) continue;
      monthLabels.push({
        label: date.toLocaleDateString([], { month: "short" }),
        weekIndex: wi,
      });
      break;
    }
  }

  return {
    weeks: trimmed,
    monthLabels,
    maxAbsDiff,
    startDay: formatLocalDay(start),
    endDay: formatLocalDay(end),
  };
}

/**
 * Discrete GitHub-like intensity 0-4 from |wordsDiff| vs the grid max.
 */
export function contributionLevel(wordsDiff: number, maxAbs: number): number {
  if (wordsDiff === 0 || maxAbs <= 0) return 0;
  const t = Math.abs(wordsDiff) / maxAbs;
  if (t <= 0.25) return 1;
  if (t <= 0.5) return 2;
  if (t <= 0.75) return 3;
  return 4;
}

export function chronicleSummary(entries: readonly ChronicleEntry[]): {
  entryCount: number;
  netWords: number;
  fileCount: number;
  firstTimestamp: string | null;
  lastTimestamp: string | null;
} {
  let netWords = 0;
  const files = new Set<string>();
  let firstTs: string | null = null;
  let lastTs: string | null = null;
  for (const entry of entries) {
    netWords += entry.wordsDiff ?? 0;
    for (const p of entry.paths ?? []) files.add(p);
    if (firstTs === null || firstTs.length === 0 || entry.timestamp < firstTs)
      firstTs = entry.timestamp;
    if (lastTs === null || lastTs.length === 0 || entry.timestamp > lastTs)
      lastTs = entry.timestamp;
  }
  return {
    entryCount: entries.length,
    netWords,
    fileCount: files.size,
    firstTimestamp: firstTs,
    lastTimestamp: lastTs,
  };
}

export function formatChronicleWhen(timestamp: string): string {
  const d = new Date(timestamp);
  if (Number.isNaN(d.getTime())) return timestamp;
  return d.toLocaleString([], {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

export function formatChronicleDay(day: string): string {
  const parts = day.split("-").map(Number);
  const y = parts[0];
  const m = parts[1];
  const d = parts[2];
  if (
    typeof y === "undefined" ||
    typeof m === "undefined" ||
    typeof d === "undefined" ||
    y === 0 ||
    m === 0 ||
    d === 0
  ) {
    return day;
  }
  return new Date(y, m - 1, d).toLocaleDateString([], {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function formatWordsDiff(diff: number): string {
  if (diff > 0) return `+${diff}`;
  return String(diff);
}

export function platformLabel(platform: ChroniclePlatform | null): string {
  if (platform === "desktop") return "Desktop";
  if (platform === "mobile") return "Mobile";
  if (platform === "web") return "Web";
  return "-";
}

export function pathLabel(path: string): string {
  return displayFileName(path);
}

/** Live-manuscript fallback matching Rust chronicle path labels. */
export function chronicleFileLabel(
  path: string,
  chapters: ZwChapter[],
): string {
  const folderM = chapterFolderFromScenePath(path);
  const sceneFileM = sceneFileFromScenePath(path);
  return matchMaybe(folderM, {
    nothing: () => displayFileName(path),
    just: (folder) =>
      matchMaybe(sceneFileM, {
        nothing: () => displayFileName(path),
        just: (sceneFile) => {
          if (/^index\.(zwdoc|md)$/i.test(sceneFile))
            return (
              chapters.find((chapter) => chapter.folder === folder)?.name ??
              displayFileName(folder)
            );
          const chapter = chapters.find((entry) => entry.folder === folder);
          if (typeof chapter === "undefined") {
            return isDefaultSceneFileName(sceneFile)
              ? `${displayFileName(folder)}, scene`
              : `${displayFileName(folder)}, ${displayFileName(sceneFile)}`;
          }
          const index = chapter.scenes.findIndex(
            (scene) => scene.file === sceneFile,
          );
          if (index < 0) {
            return isDefaultSceneFileName(sceneFile)
              ? `${displayFileName(folder)}, scene`
              : `${displayFileName(folder)}, ${displayFileName(sceneFile)}`;
          }
          const scene = chapter.scenes[index]!;
          const sceneName = sceneListName(scene);
          return `${chapter.name}, ${sceneName}`;
        },
      }),
  });
}

/**
 * Prefer snapshotted `pathLabels`; else resolve chapter/scene from the live
 * manuscript when provided; else basename.
 */
export function resolveEntryPathLabels(
  entry: Pick<ChronicleEntry, "paths" | "pathLabels">,
  chapters?: ZwChapter[],
): string[] {
  const paths = entry.paths ?? [];
  if (paths.length === 0) return [];
  const stored = entry.pathLabels;
  if (typeof stored !== "undefined" && stored.length === paths.length) {
    return [...stored];
  }
  if (typeof chapters !== "undefined" && chapters.length > 0) {
    return paths.map((p) => chronicleFileLabel(p, chapters));
  }
  return paths.map(pathLabel);
}

export function pathsLabel(
  paths: string[],
  pathLabels?: string[],
  chapters?: ZwChapter[],
): string {
  const entry: Pick<ChronicleEntry, "paths" | "pathLabels"> =
    typeof pathLabels !== "undefined" ? { paths, pathLabels } : { paths };
  const labels = resolveEntryPathLabels(entry, chapters);
  return labels.length !== 0 ? labels.join(", ") : "-";
}

export function formatEntryPaths(
  entry: Pick<ChronicleEntry, "paths" | "pathLabels">,
  chapters?: ZwChapter[],
): string {
  return pathsLabel(entry.paths ?? [], entry.pathLabels, chapters);
}
